import { fakedCodelists } from '@administration/codelists/codelists.interface';
import {
    Component,
    ComponentRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewContainerRef
} from '@angular/core';
import { ColumnSettings, EditableColumnSettings, GridCellContext } from '@common/interfaces/column-settings.interface';
import { ValueOf } from '@common/models/util.interface';
import { ViewMode } from '@common/models/view-mode';
import { environment } from '@environments/environment.base';
import { NumberFormatOptions } from '@progress/kendo-angular-intl';
import { ReferenceModel } from 'app/invoice/models/reference.model';

@Component({
    selector: 'app-grid-cell-template',
    templateUrl: './grid-cell-template.component.html'
})
export class GridCellTemplateComponent implements OnInit, OnChanges, OnDestroy {
    @Input() defaultViewMode: ValueOf<typeof ViewMode>;
    @Input() parentRoute: string;
    @Input() row: any;
    @Input() column: ColumnSettings | EditableColumnSettings;
    defaultTimeFormat: string = environment.settings.appControl.timeFormat;
    fakedCodelistArray: string[] = Object.entries(fakedCodelists).map(([, value]) => value);

    private componentRef: ComponentRef<any> | null = null;

    get references(): ReferenceModel[] {
        const referenceIds = this.row?.referenceIds?.split(';');
        const references = this.row?.references?.split(';');
        const metas = this.row?.metas?.split(';');

        const length = referenceIds?.length;
        if (!length || length === 0) return [];

        const referenceModels: ReferenceModel[] = [];
        for (let i = 0; i < length; i++) {
            referenceModels.push({ route: this.row.route, permission: this.row.permission } as ReferenceModel);

            if (referenceIds && referenceIds[i]) referenceModels[i].referenceId = +referenceIds[i];
            if (references && references[i]) referenceModels[i].reference = references[i];
            if (metas && metas[i]) referenceModels[i].meta = metas[i];
        }

        return referenceModels;
    }

    constructor(private viewContainerRef: ViewContainerRef) {}

    ngOnInit() {
        if (this.column.templateComponent) {
            this.createTemplateComponent();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        // Only update if:
        // 1. The 'row' input has changed
        // 2. It's not the first change (initial creation is handled by ngOnInit)
        if (changes['row'] && !changes['row'].firstChange) {
            this.updateTemplateComponent();
        }
    }

    ngOnDestroy() {
        if (this.componentRef) {
            this.componentRef.destroy();
        }
    }

    getCurrencyFormat(options: Partial<NumberFormatOptions> = {}): NumberFormatOptions {
        return environment.settings.appControl.currencyFormat(options);
    }

    private createTemplateComponent() {
        this.viewContainerRef.clear();

        const context: GridCellContext = {
            row: this.row,
            column: this.column,
            parentRoute: this.parentRoute,
            defaultViewMode: this.defaultViewMode
        };

        this.componentRef = this.viewContainerRef.createComponent(this.column.templateComponent);
        this.componentRef.instance.context = context;

        if (this.column.templateInputs) {
            const inputs = this.column.templateInputs(this.row);
            Object.assign(this.componentRef.instance, inputs);
        }
    }

    // ? Is this performant enough?
    // Updates the dynamic component when row data changes
    private updateTemplateComponent() {
        // Only update if we have both:
        // 1. A reference to an existing component
        // 2. A templateInputs function to generate new input values
        if (this.componentRef && this.column.templateInputs) {
            // Generate new input values based on the updated row
            const inputs = this.column.templateInputs(this.row);

            // Update the component instance with new values
            Object.assign(this.componentRef.instance, inputs);

            // Force Angular to check for changes and update the view
            // This is necessary because we're manually updating component properties
            this.componentRef.changeDetectorRef.detectChanges();
        }
    }
}
